import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { UserView } from "../../../types/store/SessionStore";
import { StoreInterfaces } from "../../../types/store/StoreInterfaces";
import LanguageToggle from "../LanguageSelectors/LanguageToggle";
import ThemeToggle from "../ThemeSelectors/ThemeToggle";

interface MenuItemDef {
  label: string;
  icon: React.ReactNode;
  navigation: string;
}

interface MenuContentProps {
  onNavigate?: () => void;
}

export default function MenuContent({ onNavigate }: Readonly<MenuContentProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSelector((state: StoreInterfaces) => state.session);

  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const menuItems = React.useMemo<MenuItemDef[]>(() => {
    if (session.isAdministrator) {
      return [
        { label: t("generic.home"), icon: <HomeRoundedIcon />, navigation: "/home" },
        { label: t("generic.accounts"), icon: <AccountBalanceRoundedIcon />, navigation: "/accounts/list" },
        { label: t("admin.accounts.account-tab-users"), icon: <GroupRoundedIcon />, navigation: "/admin/users" },
        { label: t("admin.accounts.account-tab-projects"), icon: <WorkRoundedIcon />, navigation: "/projects/list" },
        { label: t("admin.accounts.account-tab-audit", "Audit"), icon: <HistoryRoundedIcon />, navigation: "/admin/audit" },
        { label: t("admin.email-logs.title"), icon: <MailRoundedIcon />, navigation: "/admin/email-logs" },
      ];
    }

    if (session.currentView === UserView.BUYER) {
      return [
        { label: t("generic.home"), icon: <HomeRoundedIcon />, navigation: "/home" },
        { label: session.accountName ?? t("generic.account"), icon: <AccountBalanceRoundedIcon />, navigation: `/accounts/${session.accountId}` },
        { label: t("generic.accounts"), icon: <GroupRoundedIcon />, navigation: "/accounts/list" },
        { label: t("admin.accounts.account-tab-projects"), icon: <WorkRoundedIcon />, navigation: "/projects/list" },
      ];
    }

    return [
      { label: t("generic.home"), icon: <HomeRoundedIcon />, navigation: "/home" },
      { label: session.accountName ?? t("generic.account"), icon: <AccountBalanceRoundedIcon />, navigation: `/accounts/${session.accountId}` },
      ...(session.currentView === UserView.SUPPLIER
        ? []
        : [{ label: t("generic.my-projects"), icon: <WorkRoundedIcon />, navigation: "/projects/list" }]),
      { label: t("generic.invited-procedures"), icon: <AssignmentRoundedIcon />, navigation: "/procedures/tender" },
    ];
  }, [session, t]);

  const itemSx = (selected: boolean) => (theme: any) => ({
    borderRadius: 2,
    ...(selected && {
      backgroundColor: theme.palette.primary.main + "30",
      color: theme.palette.primary.main,
      "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
      "& .MuiTypography-root": { fontWeight: 700 },
      "&:hover": { backgroundColor: theme.palette.primary.main + "5" },
    }),
    "&:hover": { backgroundColor: theme.palette.action.hover },
  });

  const sectionLabelSx = {
    color: "text.secondary",
    textTransform: "uppercase",
    fontWeight: 700,
  } as const;

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: "space-between" }}>
      <List dense>
        {menuItems.map((item) => (
          <ListItem key={item.navigation} disablePadding sx={{ display: "block" }}>
            <ListItemButton
              selected={location.pathname.startsWith(item.navigation)}
              onClick={() => navigate(item.navigation)}
              sx={itemSx(location.pathname.startsWith(item.navigation))}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "inherit", "& svg": { fontSize: 20 } }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} slotProps={{ primary: { variant: "body2" } }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <List dense>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton onClick={() => setSettingsOpen((prev) => !prev)} sx={itemSx(false)}>
            <ListItemIcon sx={{ minWidth: 32, "& svg": { fontSize: 20 } }}>
              <SettingsRoundedIcon />
            </ListItemIcon>
            <ListItemText primary={t("generic.settings")} slotProps={{ primary: { variant: "body2" } }} />
            {settingsOpen ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
          </ListItemButton>
        </ListItem>

        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 2, pr: 1, py: 1 }}>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={sectionLabelSx}>
                  {t("generic.appearance")}
                </Typography>
                <ThemeToggle />
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" sx={sectionLabelSx}>
                  {t("generic.language")}
                </Typography>
                <LanguageToggle />
              </Stack>
            </Stack>
          </Box>
        </Collapse>
      </List>
    </Stack>
  );
}